import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Play, Save, Wand2, Server, ListChecks, FlaskConical, Layers, CheckCircle2, Circle, KeyRound } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SUBTABS = [
  ["overview", "Overview", Layers], ["generator", "Image Generator", Wand2],
  ["route", "Route & Models", Server], ["setup", "Gemini Setup", ListChecks],
  ["playground", "Playground", FlaskConical], ["platform", "API Platform", KeyRound],
];
const SETUP_STEPS = [
  ["Create / select a Google Cloud project", "https://console.cloud.google.com/projectcreate"],
  ["Link a billing account (paid Gemini usage is billed to the project)", "https://console.cloud.google.com/billing"],
  ["Enable the Generative Language / Gemini API", "https://console.cloud.google.com/apis/library/generativelanguage.googleapis.com"],
  ["Create an API key (or service-account credential)", "https://aistudio.google.com/app/apikey"],
  ["Add the credential to CaptionMotion (Secrets → GOOGLE_API_KEY / EMERGENT_LLM_KEY)", null],
  ["Select the image model in Route & Models", null],
  ["Run Test Connection below", null],
];

export default function CaptionMotionModel() {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);
  const [checks, setChecks] = useState({});
  const [history, setHistory] = useState([]);
  // generator state
  const [prompt, setPrompt] = useState("A cinematic South Indian wedding portrait, warm golden light");
  const [ar, setAr] = useState("9:16");
  const [res, setRes] = useState(1024);
  const [nImg, setNImg] = useState(1);
  const [genBusy, setGenBusy] = useState(false);
  const [genImgs, setGenImgs] = useState([]);
  const [pgResp, setPgResp] = useState(null);

  const load = async () => {
    try { const d = await api.cmConfig(); setData(d); } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  const loadHistory = async () => { try { const h = await api.cmHistory(); setHistory(h.requests || []); } catch {} };
  useEffect(() => { load(); loadHistory(); }, []);

  const cfg = data?.config;
  const route = cfg?.versions?.find((v) => v.version === cfg.active_version)?.route || {};
  const caps = cfg?.capabilities || {};
  const providers = data?.providers || [];
  const routeProvider = providers.find((p) => p.id === route.provider);

  const saveRoute = async (patch) => {
    setSaving(true);
    try {
      const versions = cfg.versions.map((v) => v.version === cfg.active_version ? { ...v, route: { ...v.route, ...patch } } : v);
      const d = await api.cmConfigUpdate({ versions });
      setData((x) => ({ ...x, config: d })); toast.success("Route updated");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };
  const saveCaps = async (key, val) => {
    setSaving(true);
    try { const d = await api.cmConfigUpdate({ capabilities: { ...caps, [key]: val } }); setData((x) => ({ ...x, config: d })); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };
  const runTest = async () => {
    setTesting(true); setTestRes(null);
    try { const r = await api.cmTest(); setTestRes(r); r.generationOk ? toast.success("Provider working") : toast.error(r.message || "Test failed"); }
    catch (e) { toast.error("Test failed"); }
    setTesting(false);
  };
  const generate = async () => {
    if (!prompt.trim()) { toast.error("Enter a prompt"); return; }
    setGenBusy(true); setGenImgs([]); setPgResp(null);
    try {
      const r = await api.cmGenerate({ model: cfg.product_model, prompt, aspect_ratio: ar, resolution: Number(res), number_of_images: Number(nImg) });
      setGenImgs(r.images || []); setPgResp(r);
      toast.success("Generated"); loadHistory();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? `${d.code}: ${d.message}` : (d || "Generation failed"));
      setPgResp(e?.response?.data || { error: "failed" });
    }
    setGenBusy(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;

  return (
    <div data-testid="cm-model">
      <div className="mb-1 flex items-center gap-2">
        <Sparkles className="text-[#00F0FF]" size={22} />
        <h2 className="font-display text-2xl font-black text-white">CaptionMotion AI Image Model</h2>
      </div>
      <p className="mb-5 text-sm text-[#A1A1AA]">Product model <code className="rounded bg-white/10 px-1.5 py-0.5 text-[#00F0FF]">{cfg.product_model}</code> — an internal abstraction over a configurable provider. The provider stays internal; change it without touching apps.</p>

      <div className="mb-6 flex flex-wrap gap-1.5">
        {SUBTABS.map(([id, label, Icon]) => (
          <button key={id} data-testid={`cm-tab-${id}`} onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold ${tab === id ? "bg-[#00F0FF] text-black" : "border border-white/10 text-[#A1A1AA] hover:text-white"}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[["Product Model", cfg.product_model], ["Active Version", `v${cfg.active_version}`],
              ["Underlying Provider", routeProvider?.name || route.provider], ["Provider Model", route.provider_model]].map(([l, v]) => (
              <div key={l} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="mb-1 text-[10px] uppercase tracking-widest text-[#A1A1AA]">{l}</div>
                <div className="truncate font-display text-base font-black text-white">{v}</div>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="mb-2 text-xs font-bold uppercase tracking-widest text-[#A1A1AA]">Capabilities (editable)</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(caps).map(([k, v]) => (
                <button key={k} data-testid={`cm-cap-${k}`} onClick={() => saveCaps(k, !v)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${v ? "bg-[#30D158]/15 text-[#30D158]" : "bg-white/5 text-[#A1A1AA]"}`}>
                  {v ? "✓" : "○"} {k.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="mb-2 text-xs font-bold uppercase tracking-widest text-[#A1A1AA]">Architecture</div>
            <div className="font-mono text-[11px] leading-relaxed text-[#D4D4D8]">
              User → CaptionMotion Image Service (orchestrator) → Route resolve → Provider adapter → image → storage → normalized result<br />
              Public contract: <span className="text-[#00F0FF]">{cfg.product_model}</span> — provider ({routeProvider?.name || route.provider}) is internal &amp; swappable.
            </div>
          </div>
        </div>
      )}

      {tab === "generator" && (
        <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
          <div className="space-y-3">
            <textarea data-testid="cm-gen-prompt" rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the image…" className="w-full rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm text-white outline-none focus:border-[#00F0FF]/50" />
            <div className="grid grid-cols-3 gap-3">
              <div><div className="mb-1 text-[10px] uppercase text-[#A1A1AA]">Aspect Ratio</div>
                <select data-testid="cm-gen-ar" value={ar} onChange={(e) => setAr(e.target.value)} className="w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-2 py-2 text-sm text-white">
                  {(cfg.limits?.allowed_aspect_ratios || ["1:1"]).map((a) => <option key={a} value={a}>{a}</option>)}
                </select></div>
              <div><div className="mb-1 text-[10px] uppercase text-[#A1A1AA]">Resolution</div>
                <input data-testid="cm-gen-res" type="number" value={res} onChange={(e) => setRes(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-2 py-2 text-sm text-white" /></div>
              {caps.number_of_images && <div><div className="mb-1 text-[10px] uppercase text-[#A1A1AA]"># Images</div>
                <input data-testid="cm-gen-n" type="number" min={1} max={cfg.limits?.max_number_of_images || 4} value={nImg} onChange={(e) => setNImg(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-2 py-2 text-sm text-white" /></div>}
            </div>
            <button data-testid="cm-gen-btn" onClick={generate} disabled={genBusy}
              className="flex items-center gap-2 rounded-xl bg-[#00F0FF] px-5 py-3 text-sm font-black text-black disabled:opacity-50">
              {genBusy ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Generate
            </button>
            <div className="grid grid-cols-2 gap-3">
              {genImgs.map((im) => (
                <a key={im.id} href={`${BACKEND}${im.url}`} target="_blank" rel="noreferrer" data-testid={`cm-gen-img-${im.id}`}>
                  <img src={`${BACKEND}${im.url}`} alt="generated" className="w-full rounded-xl border border-white/10" />
                </a>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="mb-2 text-xs font-bold uppercase tracking-widest text-[#A1A1AA]">Recent Generations</div>
            <div className="space-y-2 max-h-[520px] overflow-y-auto">
              {history.length === 0 && <div className="text-xs text-[#A1A1AA]">No generations yet.</div>}
              {history.map((h) => (
                <div key={h.id} className="rounded-lg border border-white/10 bg-black/30 p-2 text-[11px]">
                  <div className="flex justify-between"><span className={h.status === "completed" ? "text-[#30D158]" : "text-[#FF8A80]"}>{h.status}</span><span className="text-[#A1A1AA]">{h.latency_ms}ms</span></div>
                  <div className="mt-0.5 line-clamp-2 text-[#D4D4D8]">{h.prompt}</div>
                  <div className="mt-0.5 text-[#71717A]">{h.aspect_ratio} · {h.width}×{h.height}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "route" && (
        <div className="max-w-2xl space-y-4">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="mb-3 text-xs font-bold uppercase tracking-widest text-[#A1A1AA]">Active Route — {cfg.product_model}@{cfg.active_version}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><div className="mb-1 text-[10px] uppercase text-[#A1A1AA]">Underlying Provider</div>
                <select data-testid="cm-route-provider" value={route.provider} onChange={(e) => { const p = providers.find((x) => x.id === e.target.value); saveRoute({ provider: e.target.value, provider_model: p?.default_model }); }}
                  className="w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-2 py-2 text-sm text-white">
                  {providers.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.cost_tier})</option>)}
                </select></div>
              <div><div className="mb-1 text-[10px] uppercase text-[#A1A1AA]">Provider Model</div>
                <select data-testid="cm-route-model" value={route.provider_model || ""} onChange={(e) => saveRoute({ provider_model: e.target.value })}
                  className="w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-2 py-2 text-sm text-white">
                  {(routeProvider?.models || []).map((m) => <option key={m} value={m}>{m}</option>)}
                  {routeProvider && !(routeProvider.models || []).includes(route.provider_model) && route.provider_model && <option value={route.provider_model}>{route.provider_model}</option>}
                </select></div>
              <div><div className="mb-1 text-[10px] uppercase text-[#A1A1AA]">Fallback Provider (optional)</div>
                <select data-testid="cm-route-fallback" value={route.fallback_provider || ""} onChange={(e) => saveRoute({ fallback_provider: e.target.value || null })}
                  className="w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-2 py-2 text-sm text-white">
                  <option value="">— none —</option>
                  {providers.filter((p) => p.id !== route.provider).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></div>
            </div>
            {saving && <div className="mt-2 flex items-center gap-1 text-xs text-[#A1A1AA]"><Loader2 size={12} className="animate-spin" /> saving…</div>}
          </div>
          <button data-testid="cm-test-btn" onClick={runTest} disabled={testing}
            className="flex items-center gap-2 rounded-xl border border-[#00F0FF]/50 px-4 py-2.5 text-sm font-bold text-[#00F0FF] disabled:opacity-50">
            {testing ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Test Connection (real generation)
          </button>
          {testRes && (
            <div className="rounded-xl border border-white/10 bg-black/30 p-4 text-xs" data-testid="cm-test-result">
              <div className={testRes.generationOk ? "text-[#30D158]" : "text-[#FF8A80]"}>{testRes.generationOk ? "✓" : "✕"} auth:{testRes.authStatus} · model:{String(testRes.modelAvailable)} · gen:{String(testRes.generationOk)} · {testRes.latencyMs}ms</div>
              <div className="mt-1 text-[#D4D4D8]">{testRes.message}</div>
            </div>
          )}
        </div>
      )}

      {tab === "setup" && (
        <div className="max-w-2xl space-y-3">
          <div className="rounded-xl border border-[#FF9F0A]/30 bg-[#FF9F0A]/5 p-3 text-xs text-[#FFCf8A]">This is a guided setup assistant — CaptionMotion cannot create Google projects/billing for you. Complete each step in Google Cloud, then confirm here. Steps that can be auto-verified use the Test Connection button.</div>
          {SETUP_STEPS.map(([label, url], i) => (
            <div key={i} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <button data-testid={`cm-setup-${i}`} onClick={() => setChecks((c) => ({ ...c, [i]: !c[i] }))} className="mt-0.5">
                {checks[i] ? <CheckCircle2 size={18} className="text-[#30D158]" /> : <Circle size={18} className="text-[#71717A]" />}
              </button>
              <div className="flex-1">
                <div className="text-sm text-white">{i + 1}. {label}</div>
                {url && <a href={url} target="_blank" rel="noreferrer" className="text-[11px] text-[#00F0FF] hover:underline">Open in Google Cloud →</a>}
                <div className="text-[10px] text-[#71717A]">{checks[i] ? "Confirmed by administrator" : "Pending"}</div>
              </div>
            </div>
          ))}
          <button data-testid="cm-setup-test" onClick={runTest} disabled={testing} className="flex items-center gap-2 rounded-xl bg-[#00F0FF] px-4 py-2.5 text-sm font-black text-black disabled:opacity-50">
            {testing ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Verify with Test Connection
          </button>
          {testRes && <div className="text-xs text-[#D4D4D8]">{testRes.generationOk ? "✓ Verified automatically — provider works." : `✕ ${testRes.message}`}</div>}
        </div>
      )}

      {tab === "playground" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            <textarea data-testid="cm-pg-prompt" rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} className="w-full rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm text-white" />
            <div className="flex gap-2">
              <select value={ar} onChange={(e) => setAr(e.target.value)} className="rounded-lg border border-white/10 bg-[#0A0A0A] px-2 py-2 text-sm text-white">
                {(cfg.limits?.allowed_aspect_ratios || ["1:1"]).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <button data-testid="cm-pg-run" onClick={generate} disabled={genBusy} className="flex items-center gap-2 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-black text-black disabled:opacity-50">{genBusy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Run</button>
            </div>
            {genImgs.map((im) => <img key={im.id} src={`${BACKEND}${im.url}`} alt="pg" className="w-full max-w-xs rounded-xl border border-white/10" />)}
          </div>
          <div>
            <div className="mb-1 text-[10px] uppercase text-[#A1A1AA]">Normalized request → response (sanitized, no secrets)</div>
            <pre data-testid="cm-pg-json" className="max-h-[460px] overflow-auto rounded-xl border border-white/10 bg-black/40 p-3 text-[11px] text-[#8AE234]">
{JSON.stringify({ request: { model: cfg.product_model, prompt, aspect_ratio: ar, resolution: Number(res) }, response: pgResp }, null, 2)}
            </pre>
          </div>
        </div>
      )}
      {tab === "platform" && <PlatformAdmin />}
    </div>
  );
}

function PlatformAdmin() {
  const [cfg, setCfg] = useState(null);
  const [overview, setOverview] = useState(null);
  const [keys, setKeys] = useState([]);
  const [daily, setDaily] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [c, o, k, dd] = await Promise.all([api.cmPlatformConfig(), api.cmPlatformOverview(), api.cmPlatformKeys(), api.cmPlatformUsageDaily(30)]);
      setCfg(c.config); setOverview(o); setKeys(k.keys || []); setDaily((dd.series || []).map((r) => ({ ...r, d: r.date.slice(5) })));
    } catch { toast.error("Failed to load platform"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try { const d = await api.cmPlatformConfigUpdate(patch); setCfg(d); toast.success("Saved"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };
  const revoke = async (id) => { try { await api.cmPlatformKeyRevoke(id); toast.success("Revoked"); load(); } catch { toast.error("Failed"); } };

  if (!cfg) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;

  const setPricing = (op, v) => save({ pricing: { ...cfg.pricing, [op]: Number(v) } });
  const setLimit = (plan, field, v) => save({ rate_limits: { ...cfg.rate_limits, [plan]: { ...cfg.rate_limits[plan], [field]: Number(v) } } });

  const box = "rounded-xl border border-white/10 bg-white/[0.03] p-4";
  const num = "w-20 rounded-md border border-white/10 bg-[#0A0A0A] px-2 py-1 text-sm text-white";

  return (
    <div className="space-y-5" data-testid="cm-platform-admin">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[["Developers", overview?.developers], ["API keys", `${overview?.active_keys}/${overview?.total_keys}`],
          ["Requests (30d)", overview?.usage?.total], ["Credits outstanding", overview?.credits_outstanding]].map(([l, v]) => (
          <div key={l} className={box}><div className="text-[10px] uppercase text-[#A1A1AA]">{l}</div><div className="mt-1 font-mono text-xl font-bold text-white">{v ?? 0}</div></div>
        ))}
      </div>

      <div className={box}>
        <div className="mb-3 font-display text-sm font-bold text-white">Platform-wide generations (30 days)</div>
        <div style={{ width: "100%", height: 200 }}>
          <ResponsiveContainer>
            <BarChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="d" tick={{ fill: "#ffffff55", fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tick={{ fill: "#ffffff55", fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#141418", border: "1px solid #ffffff20", fontSize: 12 }} />
              <Bar dataKey="successful" stackId="a" fill="#00F0FF" radius={[2, 2, 0, 0]} />
              <Bar dataKey="failed" stackId="a" fill="#ff5470" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className={box}>
        <div className="mb-3 font-display text-sm font-bold text-white">Platform controls</div>
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" checked={!!cfg.new_keys_enabled} onChange={(e) => save({ new_keys_enabled: e.target.checked })} data-testid="cm-plat-newkeys" /> Allow new API keys
          </label>
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" checked={!!cfg.admin_bypass} onChange={(e) => save({ admin_bypass: e.target.checked })} data-testid="cm-plat-bypass" /> Admin unlimited (bypass credits &amp; limits)
          </label>
          <label className="flex items-center gap-2 text-sm text-white/80">
            Starter credits <input type="number" className={num} defaultValue={cfg.starter_credits} onBlur={(e) => save({ starter_credits: Number(e.target.value) })} data-testid="cm-plat-starter" />
          </label>
        </div>
      </div>

      <div className={box}>
        <div className="mb-3 font-display text-sm font-bold text-white">Pricing (credits per image)</div>
        <div className="flex flex-wrap gap-4">
          {Object.entries(cfg.pricing).map(([op, v]) => (
            <label key={op} className="flex items-center gap-2 text-sm text-white/80"><span className="font-mono text-xs text-[#00F0FF]">{op}</span>
              <input type="number" className={num} defaultValue={v} onBlur={(e) => setPricing(op, e.target.value)} data-testid={`cm-plat-price-${op}`} /></label>
          ))}
        </div>
      </div>

      <div className={box}>
        <div className="mb-3 font-display text-sm font-bold text-white">Rate limits per plan</div>
        {["free", "pro"].map((plan) => (
          <div key={plan} className="mb-2 flex flex-wrap items-center gap-3">
            <span className="w-12 text-xs font-bold uppercase text-white/60">{plan}</span>
            {["per_minute", "per_hour", "per_day", "concurrent"].map((f) => (
              <label key={f} className="flex items-center gap-1.5 text-xs text-white/70">{f.replace("per_", "/")}
                <input type="number" className={num} defaultValue={cfg.rate_limits?.[plan]?.[f]} onBlur={(e) => setLimit(plan, f, e.target.value)} data-testid={`cm-plat-rl-${plan}-${f}`} /></label>
            ))}
          </div>
        ))}
      </div>

      <div className={box}>
        <div className="mb-3 font-display text-sm font-bold text-white">All API keys ({keys.length})</div>
        <div className="max-h-80 overflow-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-[10px] uppercase text-[#A1A1AA]"><tr><th className="py-2">Owner</th><th>Key</th><th>Env</th><th>Status</th><th></th></tr></thead>
            <tbody data-testid="cm-plat-keys">
              {keys.map((k) => (
                <tr key={k.id} className="border-t border-white/5">
                  <td className="py-2 text-white/70">{k.email}</td>
                  <td className="font-mono text-white/50">{k.masked}</td>
                  <td className="text-white/50">{k.environment}</td>
                  <td>{k.revoked ? <span className="text-red-400">revoked</span> : k.disabled ? <span className="text-amber-400">disabled</span> : <span className="text-[#00F0FF]">active</span>}</td>
                  <td className="text-right">{!k.revoked && <button onClick={() => revoke(k.id)} className="rounded border border-red-500/30 px-2 py-1 text-red-400 hover:bg-red-500/10" data-testid={`cm-plat-revoke-${k.id}`}>Revoke</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {saving && <div className="text-xs text-white/40">Saving…</div>}
    </div>
  );
}
